/**
 * What the toolbar says while something is ticked — list engine v3 §2.4.
 *
 * It takes the place of the `128 of 422` count, in the **same slot, on the
 * same row**. That is the whole design: the thing being replaced inserted a
 * bar above the table when a row was ticked, which pushed the table down by
 * forty pixels — so the row you were about to tick next was no longer under
 * your pointer, and ticking three rows meant three corrections. Nothing moves
 * now. The count becomes a sentence about the selection and goes back to
 * being a count when you clear it.
 *
 * `Select all N` names the **filtered** number, never the tenant's total: on
 * a list showing 128 of 422 it offers 128, because the 422 are not what the
 * person is looking at and offering them is how somebody deactivates three
 * hundred records they never saw.
 */
import { IconCheck, IconClose } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import type { Selection } from '../lib/selection.js'

export interface SelectionSlotProps {
  selection: Selection
  count: number
  /** What the list is showing — the number `Select all` offers. */
  visibleCount: number
  totalCount: number
  onSelectAll: () => void
  onClear: () => void
  /** Narrow: the count and `Select all` go, leaving "3 selected · Clear". */
  tight?: boolean
}

export function SelectionSlot({
  selection, count, visibleCount, totalCount, onSelectAll, onClear, tight,
}: SelectionSlotProps) {
  if (count === 0) {
    return (
      <span className="tnum whitespace-nowrap text-cell text-text-muted">
        {formatNumber(visibleCount)} of {formatNumber(totalCount)}
      </span>
    )
  }

  const left = selection.mode === 'matching' ? selection.excluded.size : 0

  return (
    <span className="flex min-w-0 shrink-0 items-center gap-2 whitespace-nowrap">
      <span className="flex items-center gap-1 text-cell font-semibold text-accent-dark">
        <IconCheck size={13} />
        {selection.mode === 'matching'
          ? `All ${formatNumber(count)} selected`
          : `${formatNumber(count)} selected`}
      </span>

      {/* Only when there is more to take. "Select all 3" over three ticked
          rows is a control that does nothing. */}
      {!tight && selection.mode === 'ids' && visibleCount > count && (
        <button
          type="button"
          onClick={onSelectAll}
          className="text-cell font-semibold text-accent hover:text-accent-dark"
        >
          Select all {formatNumber(visibleCount)}
        </button>
      )}

      {!tight && left > 0 && (
        <span className="tnum text-cell text-text-muted">
          · {formatNumber(left)} left out
        </span>
      )}

      <button
        type="button"
        onClick={onClear}
        className={cn('flex items-center gap-1 text-cell text-text-muted',
          'hover:text-text')}
      >
        <IconClose size={11} />
        Clear
      </button>
    </span>
  )
}
